import { create } from "zustand";
import { axiosInstance } from "../lib/axios";
import toast from "react-hot-toast";
import { useAuthStore } from "./useAuthStore";

export const useRelationshipStore = create((set, get) => ({
  pairingCode: null,
  codeExpiresAt: null,
  pendingRequests: [],
  isLoading: false,

  generateCode: async () => {
    set({ isLoading: true });
    try {
      const res = await axiosInstance.post("/relationship/generate-code");
      set({ pairingCode: res.data.code, codeExpiresAt: res.data.expiresAt });
      toast.success("Pairing code generated!");
    } catch (error) {
      toast.error(error.response?.data?.message || "Failed to generate code");
    } finally {
      set({ isLoading: false });
    }
  },

  linkCode: async (code) => {
    set({ isLoading: true });
    try {
      const res = await axiosInstance.post("/relationship/link-code", { pairingCode: code });
      toast.success(res.data.message);
      // Refresh auth user to get updated status and partnerId
      await useAuthStore.getState().checkAuth();
      return true;
    } catch (error) {
      toast.error(error.response?.data?.message || "Failed to link code");
      return false;
    } finally {
      set({ isLoading: false });
    }
  },

  sendExclusiveRequest: async (receiverId) => {
    set({ isLoading: true });
    try {
      const res = await axiosInstance.post("/relationship/request", { receiverId });
      toast.success("Exclusive request sent! 💍");
    } catch (error) {
      toast.error(error.response?.data?.message || "Failed to send request");
    } finally {
      set({ isLoading: false });
    }
  },

  getPendingRequests: async () => {
    set({ isLoading: true });
    try {
      const res = await axiosInstance.get("/relationship/requests");
      set({ pendingRequests: res.data });
    } catch (error) {
      console.error("Error fetching requests:", error);
    } finally {
      set({ isLoading: false });
    }
  },

  acceptRequest: async (requestId) => {
    set({ isLoading: true });
    try {
      const res = await axiosInstance.post("/relationship/accept", { requestId });
      toast.success("Congratulations! You are now coupled! 🎉");
      set({ pendingRequests: get().pendingRequests.filter(req => (req._id || req.id) !== requestId) });
      await useAuthStore.getState().checkAuth();
    } catch (error) {
      toast.error(error.response?.data?.message || "Failed to accept request");
    } finally {
      set({ isLoading: false });
    }
  },

  rejectRequest: async (requestId) => {
    set({ isLoading: true });
    try {
      await axiosInstance.post(`/relationship/reject/${requestId}`);
      set({ pendingRequests: get().pendingRequests.filter(req => (req._id || req.id) !== requestId) });
      toast.success("Request rejected");
    } catch (error) {
      toast.error(error.response?.data?.message || "Failed to reject request");
    } finally {
      set({ isLoading: false });
    }
  },

  unlinkPartner: async () => {
    set({ isLoading: true });
    try {
      await axiosInstance.post("/relationship/unlink");
      await useAuthStore.getState().checkAuth();
      toast.success("Disconnected — welcome back to discovery mode!");
      return true;
    } catch (error) {
      toast.error(error.response?.data?.message || "Failed to unlink");
      return false;
    } finally {
      set({ isLoading: false });
    }
  }
}));
